import { Injectable } from '@angular/core';
import { CoordinateSystem, type Feature, type GeoCalculationService } from '@next/arable-lib/geo';
import type { Coordinate } from 'ol/coordinate';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { transform } from 'ol/proj';
import { getArea } from 'ol/sphere';

@Injectable({
  providedIn: 'root',
})
export class AppGeoCalculationBrowserService implements GeoCalculationService {
  getFeatureCenter(feature: Feature): Coordinate | null {
    const geoFeature = feature ? new GeoJSON().readFeature(feature) : null;
    const pseudoMercatorCenter = geoFeature?.getGeometry() ? getCenter(geoFeature.getGeometry().getExtent()) : null;
    const worldGeodeticCenter = pseudoMercatorCenter
      ? transform(pseudoMercatorCenter, CoordinateSystem.PseudoMercator, CoordinateSystem.WorldGeodetic)
      : null;

    return worldGeodeticCenter;
  }

  getAreaInHectares(featureEntity: Feature): number {
    if (!featureEntity) {
      return 0;
    }

    const feature = new GeoJSON().readFeature(featureEntity);
    const geometry = feature.getGeometry();
    const area = geometry ? getArea(geometry) : 0;

    return Math.round((area / 10000) * 100) / 100;
  }

  getBoundingBoxWithPadding(coords: number[][], paddingFactor = 0.3): [Coordinate, Coordinate] | null {
    if (Number.isNaN(paddingFactor)) {
      paddingFactor = 0.3;
    }

    if (!coords || coords.length === 0 || coords[0].length !== 2 || paddingFactor < 0) {
      return null;
    }

    let minX = coords[0][0];
    let maxX = coords[0][0];
    let minY = coords[0][1];
    let maxY = coords[0][1];

    coords.forEach((coord) => {
      if (coord.length >= 2) {
        if (coord[0] < minX) {
          minX = coord[0];
        }

        if (coord[0] > maxX) {
          maxX = coord[0];
        }

        if (coord[1] < minY) {
          minY = coord[1];
        }

        if (coord[1] > maxY) {
          maxY = coord[1];
        }
      }
    });

    const width = Math.abs(maxX - minX);
    const height = Math.abs(maxY - minY);
    const paddingX = width * paddingFactor;
    const paddingY = height * paddingFactor;
    const topLeft: Coordinate = [minX - paddingX, maxY + paddingY];
    const bottomRight: Coordinate = [maxX + paddingX, minY - paddingY];

    return [topLeft, bottomRight];
  }
}
